import { useTranslation } from "react-i18next"

import type { LucideIcon } from "@/components/icons"

import { ArrowRight, FileSearch, FileText, Receipt, ScrollText } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ensureArray } from "@/features/training/utils/ensure-array"

type UseCase = {
  value: "invoices" | "contracts" | "incidents"
  icon: LucideIcon
  labelKey: string
  docTitleKey: string
  docExcerptKey: string
  columnsKey: string
  rowsKey: string
}

type ExtractedRow = {
  label: string
  value: string
  type: "text" | "number" | "date" | "yesno"
}

const USE_CASES: ReadonlyArray<UseCase> = [
  {
    value: "invoices",
    icon: Receipt,
    labelKey: "case_invoices_label",
    docTitleKey: "case_invoices_doc_title",
    docExcerptKey: "case_invoices_doc_excerpt",
    columnsKey: "case_invoices_columns",
    rowsKey: "case_invoices_rows",
  },
  {
    value: "contracts",
    icon: ScrollText,
    labelKey: "case_contracts_label",
    docTitleKey: "case_contracts_doc_title",
    docExcerptKey: "case_contracts_doc_excerpt",
    columnsKey: "case_contracts_columns",
    rowsKey: "case_contracts_rows",
  },
  {
    value: "incidents",
    icon: FileSearch,
    labelKey: "case_incidents_label",
    docTitleKey: "case_incidents_doc_title",
    docExcerptKey: "case_incidents_doc_excerpt",
    columnsKey: "case_incidents_columns",
    rowsKey: "case_incidents_rows",
  },
] as const

function TypeBadge({ type }: { type: ExtractedRow["type"] }) {
  const { t } = useTranslation("", {
    keyPrefix: "training.ai-parser.getting-started.use_cases_demo",
  })
  return (
    <Badge className="h-4 px-1.5 text-[10px] font-medium" variant="secondary">
      {t(`type_${type}`)}
    </Badge>
  )
}

export function DemoParserUseCases() {
  const { t } = useTranslation("", {
    keyPrefix: "training.ai-parser.getting-started.use_cases_demo",
  })

  const rows = (key: string) => ensureArray<ExtractedRow>(t(key, { returnObjects: true }))
  const columns = (key: string) => ensureArray<string>(t(key, { returnObjects: true }))

  return (
    <div className="w-full">
      <Tabs className="w-full" defaultValue="invoices">
        <TabsList className="w-full flex-wrap justify-start gap-1">
          {USE_CASES.map(({ value, icon: Icon, labelKey }) => (
            <TabsTrigger key={value} value={value}>
              <Icon className="size-4" />
              <span>{t(labelKey)}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {USE_CASES.map(({ value, docTitleKey, docExcerptKey, columnsKey, rowsKey }) => {
          const extractedRows = rows(rowsKey)
          const definedColumns = columns(columnsKey)

          return (
            <TabsContent key={value} value={value}>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="bg-muted/30 flex flex-col gap-2 rounded-xl border p-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground text-[10px] font-bold tracking-wide uppercase">
                      {t("before_label", "Source document")}
                    </span>
                    <Badge variant="secondary">
                      <FileText className="mr-1 size-3" />
                      {t(docTitleKey)}
                    </Badge>
                  </div>
                  <pre className="text-foreground/80 bg-background/60 mt-1 overflow-x-auto rounded-md border p-3 font-mono text-[11px] leading-relaxed whitespace-pre-wrap">
                    {t(docExcerptKey)}
                  </pre>
                  <div className="text-muted-foreground mt-1 text-[10px]">
                    {t("defined_columns_label", "Defined columns:")}{" "}
                    <span className="text-foreground font-medium">
                      {definedColumns.join(" · ")}
                    </span>
                  </div>
                </div>

                <div className="bg-primary/5 border-primary/30 flex flex-col gap-2 rounded-xl border p-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-primary flex items-center gap-1.5 text-[10px] font-bold tracking-wide uppercase">
                      <ArrowRight className="size-3.5" />
                      {t("after_label", "Extracted table row")}
                    </span>
                    <Badge className="bg-primary/15 text-primary border-primary/30">
                      {t("structured_label", "Structured")}
                    </Badge>
                  </div>
                  <div className="bg-background/80 rounded-md border">
                    {extractedRows.map((row, i) => (
                      <div
                        key={i}
                        className={
                          i < extractedRows.length - 1
                            ? "flex items-center justify-between gap-3 border-b px-3 py-2"
                            : "flex items-center justify-between gap-3 px-3 py-2"
                        }
                      >
                        <div className="flex min-w-0 flex-col">
                          <span className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
                            {row.label}
                          </span>
                          <span className="truncate text-sm font-medium">{row.value}</span>
                        </div>
                        <TypeBadge type={row.type} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>
          )
        })}
      </Tabs>
    </div>
  )
}
